import { Location } from '@angular/common';
import {
    HttpErrorResponse,
    type HttpHandler,
    type HttpHeaderResponse,
    type HttpInterceptor,
    type HttpProgressEvent,
    type HttpRequest,
    type HttpResponse,
    type HttpSentEvent,
    HttpStatusCode,
    type HttpUserEvent,
} from '@angular/common/http';
import { computed, Injectable, Injector, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, EMPTY, type Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';

import { AuthenticationService } from '@app/auth/authentication.service';
import { LOGIN_PATH, REQUEST_OTP_PATH } from '@app/shared/api/api-route.config';
import { AppConfigService } from '@app/shared/api/app-config.service';

const headerKeys = {
    Authorization: 'Authorization',
    Language: 'x-language',
};

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private readonly isRefreshingToken = signal(false);

    private readonly tokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    private readonly manualRefreshReminderTimeout = computed(
        () => this.appConfigService.appConfig().manualRefreshReminderTimeout || 0,
    );

    private readonly appConfigService = inject(AppConfigService);
    private readonly injector = inject(Injector);
    private readonly router = inject(Router);
    private readonly location = inject(Location);

    private isRefreshTokenURL(url: string): boolean {
        return url.endsWith('token_refresh');
    }

    private isWhoamiURL(url: string): boolean {
        return url.endsWith('whoami');
    }

    private isLoginURL(url: string): boolean {
        // When login throws 401, component captures it to show error in the page
        return url.endsWith(LOGIN_PATH) || url.endsWith(REQUEST_OTP_PATH);
    }

    private addToken(req: HttpRequest<any>, token?: string): HttpRequest<any> {
        /* If withCredentials is present that means refresh token request is
          sent with httpOnly cookies. Instead of adding token pass request as is.
          Else add token to every request. */
        if (req.withCredentials) {
            return req;
        }
        if (token === undefined) {
            const authService = this.injector.get(AuthenticationService);
            token = authService.getToken();
        }
        if (!token) {
            // If no token or token is null - don't sent Auth header
            return req;
        }

        const headers = {
            [headerKeys.Authorization]: `JWT ${token}`,
            [headerKeys.Language]: navigator.language,
        };

        return req.clone({
            setHeaders: headers,
        });
    }

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler,
    ): Observable<
        | HttpSentEvent
        | HttpHeaderResponse
        | HttpProgressEvent
        | HttpResponse<any>
        | HttpUserEvent<any>
    > {
        const INVALID_URL_PARAMS = ['/undefined', '/NaN', '/null'];
        if (INVALID_URL_PARAMS.some(r => req.url.includes(r))) {
            console.error('Invalid Request ', req.url);

            return EMPTY;
        }

        const authService = this.injector.get(AuthenticationService);

        return next.handle(this.addToken(req)).pipe(
            catchError(error => {
                const token = authService.getToken();

                // Refresh token handling is done as per the method described in
                // https://www.intertech.com/Blog/angular-4-tutorial-handling-refresh-token-with-new-httpinterceptor/
                if (error instanceof HttpErrorResponse) {
                    if (this.isRefreshTokenURL(req.url)) {
                        // A refresh token request failed (Example: Refresh token expired)
                        // Keep the API error response intact so the UI can display its fallback message.
                        // force users to see the 404 /not-found page
                        return throwError(() => new HttpErrorResponse(error));
                    }
                    if (
                        token &&
                        error.status === Number(HttpStatusCode.Unauthorized) &&
                        !this.isLoginURL(req.url)
                    ) {
                        // If user has not logged out before (i.e. an existing token exists)
                        // And the Request failed due to invalid token (timeout normally)
                        // And the URL is not login (because in login, user can enter wrong password to get UNAUTH)
                        return this.handleInvalidToken(req, next);
                    }
                    if (!token && !this.isWhoamiURL(req.url) && !this.isLoginURL(req.url)) {
                        // If no token found, and we are not expected to refresh or show better errors, just log them out.
                        // Can happen if user logged out in another tab and current tab should also logout
                        this.navigateToLogout();
                    }
                    return throwError(() => new HttpErrorResponse(error));
                }

                return throwError(() => new HttpErrorResponse(error));
            }),
        );
    }

    private navigateToLogout() {
        // NOTE: We have found that routerState's URL is '' sometimes.
        //       This may be because there is no component that is loaded ?
        //       For now - we are using location.path() in case that is empty
        const returnUrl = this.router.routerState.snapshot.url || this.location.path(true);

        this.router.navigate(['/logout'], {
            queryParams: { returnUrl },
        });
    }

    private handleInvalidToken(req: HttpRequest<any>, next: HttpHandler) {
        const authService = this.injector.get(AuthenticationService);

        // In case of manual, force logout the user after token expires since we have continue
        // session pop up to refresh the token and avoid auto refresh token call
        if (this.manualRefreshReminderTimeout() > 0) {
            this.navigateToLogout();

            return EMPTY;
        }

        if (!this.isRefreshingToken()) {
            this.isRefreshingToken.set(true);

            // Reset here so that the following requests wait until the token
            // comes back from the refreshToken call.
            this.tokenSubject.next(null);

            return authService.refreshToken().pipe(
                switchMap((refreshTokenResult: any) => {
                    const newToken = refreshTokenResult.access_token;
                    if (newToken) {
                        this.tokenSubject.next(newToken);
                        authService.saveTokens(refreshTokenResult);
                        // Load app configs after token refresh
                        return this.appConfigService
                            .load()
                            .pipe(switchMap(() => next.handle(this.addToken(req, newToken))));
                    }

                    // If we don't get a new token, we are in trouble so logout.
                    this.navigateToLogout();

                    return EMPTY;
                }),
                catchError(() => {
                    // If there is an exception calling 'refreshToken', bad news so logout.
                    this.navigateToLogout();

                    return EMPTY;
                }),
                finalize(() => this.isRefreshingToken.set(false)),
            );
        }

        return this.tokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap(token => next.handle(this.addToken(req, token))),
        );
    }
}
